import {
  UpdateEventListener,
  FireableUpdateEventListener
} from 'utils/UpdateEventListener';
import _ from 'lodash';
import i18n from 'i18next';
import {
  DefaultCreativeListModel,
  CreativeListColumns,
  CreativeListModel
} from 'components/CampaignCreativeList/CreativeListModel';
import rtbBindingListFormatters from 'components/CampaignCreativeList/defaultListFormatters';
import { CreativeManagementStateContext } from './CreativeManagementStateContext';
import { L1Object } from 'core/l1Object/L1Object';
import {
  isArchivedCampaignCreative,
  isOneForAllCreativeType
} from 'core/creative/Creative';
import { CreativeManagementData } from 'core/rtbCampaign/CreativeBindData';
import {
  isEpaperChannelGroup,
  isEpaperSpaceQuotaFull
} from 'core/channelGroup/EpaperChannelGroup';

export interface ModifyCreativeActivationStateContentModel {
  readonly state: ModifyCreativeActivationStateContentState;
  readonly event: UpdateEventListener<ModifyCreativeActivationStateContentModel>;
  readonly creativeData: CreativeManagementData[];
  readonly activate: boolean;
  readonly selectedCreatives: number[];
  /**
   * 這次啟用會不會讓某個活動出現兩份以上啟用中的素材。
   *
   * 「批次編輯 > 素材 > 啟用」不會經過 CreativeOverviewStateContent，是
   * initModifyActivationStates 直接開在選素材畫面，那邊的 canActive 完全跑不到。
   * 跟 BindCreativeState 的 hasActiveCreative 是同一條限制的另一半：那邊擋綁進來，
   * 這邊擋既有素材被開起來。
   */
  readonly exceedsSingleActiveLimit: boolean;
  /**
   * 這次啟用會開在「機台名額已滿」的活動上時，那幾檔活動的名稱。
   *
   * 與 exceedsSingleActiveLimit 同一個位置存在的理由：這個畫面是
   * initModifyActivationStates 直接開的，總覽頁的 canActive 完全跑不到。差別在問的問題
   * 不同 —— 那條問「會不會變成兩份啟用中」，這條問「開起來排得進機台嗎」。
   *
   * 回名稱而不是布林：多檔活動在這個畫面是分頁的，只講「有活動滿了」的話使用者得一頁
   * 一頁翻才知道是哪一檔。
   */
  readonly quotaFullCampaignNames: string[];
  setStateContext(stateContext: CreativeManagementStateContext): void;
  getTitle(): string;
  handleOnSelect(creativeId: number): void;
  handleOnSelectAll(): void;
  onCampaignChange(index: number): void;
  getCreativeListModel(): CreativeListModel;
}

export type ModifyCreativeActivationStateContentProps = {
  readonly model: ModifyCreativeActivationStateContentModel;
};

export type ModifyCreativeActivationStateContentState = {
  readonly selectedCreatives: number[];
  readonly currentCampaignIndex: number;
};

export class DefaultModifyCreativeActivationStateContentModel implements ModifyCreativeActivationStateContentModel {
  event: FireableUpdateEventListener<ModifyCreativeActivationStateContentModel>;
  creativeData: CreativeManagementData[];
  stateContext?: CreativeManagementStateContext;
  selectedCreatives: number[];
  currentCampaignIndex: number;
  bindingIdMap: Record<number, string> = {};
  /**
   * 每個活動目前有幾份啟用中的素材，key 是活動 id。
   *
   * this.creativeData 已經被過濾成「還可以切換的那些」（啟用時只留停用中的素材），
   * 算既有啟用數要用進來時的原始清單，不然永遠算成 0。
   */
  activeCreativeCountMap: Record<number, number> = {};

  constructor(
    creativeData: CreativeManagementData[],
    public activate: boolean,
    private l1Object: L1Object
  ) {
    this.event =
      new FireableUpdateEventListener<ModifyCreativeActivationStateContentModel>();
    this.creativeData = creativeData.map((data) => {
      return {
        ...data,
        creatives: _.filter(
          data.creatives,
          (creative) =>
            creative.isActiveBinding === !activate &&
            !(activate && isOneForAllCreativeType(creative.creativeType))
        )
      };
    });
    this.selectedCreatives = [];
    this.currentCampaignIndex = 0;
    creativeData.forEach((data) => {
      this.activeCreativeCountMap[data.id] = data.creatives.filter(
        (creative) => creative.isActiveBinding
      ).length;
      data.creatives.forEach((creative) => {
        this.bindingIdMap[`${data.id}-${creative.id}`] = creative.l3ChannelId;
      });
    });
  }

  /**
   * 這個活動這次被選到幾份素材。
   *
   * selectedCreatives 是跨活動共用的一份素材 id 清單，要先跟這個活動的清單取交集，才不會
   * 把別的活動選到的素材算進來。回 0 代表這個活動這次沒被動到 —— 兩條限制都據此跳過它：
   * 本來就超標（例如限制上線前留下的資料）也不該讓整批動作永遠按不下去，使用者沒有辦法
   * 在這個畫面把它排除掉。
   */
  private selectedCountOf(data: CreativeManagementData): number {
    return _.intersection(
      data.creatives.map((creative) => creative.id),
      this.selectedCreatives
    ).length;
  }

  get exceedsSingleActiveLimit(): boolean {
    if (!this.activate) {
      return false;
    }
    return this.creativeData.some((data) => {
      if (!isEpaperChannelGroup(data.channelGroupId)) {
        return false;
      }
      const selectedCount = this.selectedCountOf(data);
      if (selectedCount === 0) {
        return false;
      }
      return (this.activeCreativeCountMap[data.id] ?? 0) + selectedCount > 1;
    });
  }

  get quotaFullCampaignNames(): string[] {
    if (!this.activate) {
      // 停用不會佔名額，反而是釋放。
      return [];
    }
    // 去重的理由見 BindCreativeStateContentModel.quotaFullCampaignNames。
    return _.uniq(
      this.creativeData
        .filter(
          (data) =>
            isEpaperSpaceQuotaFull(data) && this.selectedCountOf(data) > 0
        )
        .map((data) => data.name)
    );
  }

  get state(): ModifyCreativeActivationStateContentState {
    return {
      selectedCreatives: this.selectedCreatives,
      currentCampaignIndex: this.currentCampaignIndex
    };
  }

  get listFormatters() {
    return rtbBindingListFormatters;
  }

  getCreativeListModel() {
    const currentCreativeData = this.creativeData[this.currentCampaignIndex];
    const columnsToShow = [
      CreativeListColumns.ID,
      CreativeListColumns.PREVIEW,
      CreativeListColumns.STATUS,
      CreativeListColumns.DELIVERY,
      CreativeListColumns.TOOL
    ];
    return new DefaultCreativeListModel(
      this.l1Object,
      {
        id: currentCreativeData.id,
        draftId: currentCreativeData.draftId,
        startDate: currentCreativeData.startDate,
        endDate: currentCreativeData.endDate,
        channel: currentCreativeData.channel,
        channelGroupId: currentCreativeData.channelGroupId
      },
      currentCreativeData.creatives,
      _.intersection(
        currentCreativeData.creatives.map((creative) => creative.id),
        this.selectedCreatives
      ),
      false,
      this.handleOnSelect,
      this.handleOnSelectAll,
      (creativeId) => `/creatives/${creativeId}/edit`,
      this.listFormatters,
      columnsToShow
    );
  }

  getTitle() {
    if (this.activate) {
      return i18n.t(
        'modifyCreativeActivationStateContent.labels.chooseActivate'
      );
    } else {
      return i18n.t(
        'modifyCreativeActivationStateContent.labels.chooseDeactivate'
      );
    }
  }

  setStateContext = (stateContext: CreativeManagementStateContext) => {
    this.stateContext = stateContext;
  };

  handleOnSelect = (creativeId: number) => {
    const creative = this.creativeData[
      this.currentCampaignIndex
    ].creatives.find((c) => c.id === creativeId);
    if (creative && isArchivedCampaignCreative(creative)) {
      return;
    }
    if (this.selectedCreatives.indexOf(creativeId) > -1) {
      _.remove(this.selectedCreatives, (id) => id === creativeId);
    } else {
      this.selectedCreatives.push(creativeId);
    }

    this.updateStateData();
    this.updateState();
  };

  handleOnSelectAll = () => {
    const allCreativeId = this.creativeData[this.currentCampaignIndex].creatives
      .filter((creative) => !isArchivedCampaignCreative(creative))
      .map((creative) => creative.id);
    const notSelected = _.difference(allCreativeId, this.selectedCreatives);
    if (notSelected.length > 0) {
      this.selectedCreatives = _.concat(this.selectedCreatives, notSelected);
    } else {
      this.selectedCreatives = _.difference(
        this.selectedCreatives,
        allCreativeId
      );
    }
    this.updateStateData();
    this.updateState();
  };

  updateStateData = () => {
    this.stateContext?.setData &&
      this.stateContext.setData({
        bindingIdMap: this.bindingIdMap,
        activate: this.activate,
        selectedCreatives: this.selectedCreatives
      });
  };

  onCampaignChange = (index: number) => {
    this.currentCampaignIndex = index;
    this.updateState();
  };

  updateState() {
    this.event.fireEvent(this);
  }
}
